import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Boxes, Telescope } from 'lucide-react';
import type { ReactNode } from 'react';
import { Explain } from '@/components/Explain';
import { Section } from '@/components/Section';
import { CopyId, STATUS_META } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap, LookupParams } from '@/lib/bootstrap';
import type { TimeZoneMode } from '@/lib/format';
import type { SourceResult, TransactionLookupInput, TransactionsResult } from '@/types';
import { ApiError, fetchClients } from '@/features/bet-inspection/lib/api';
import { DataTable } from '@/features/bet-inspection/components/sections/DataTable';
import { FilterBar } from './components/FilterBar';
import { fetchTransactions } from './lib/api';
import { TX_SOURCES, toggleTxSource, useSelectedTxSources, type TxSourceKey } from './lib/sources';

/**
 * Transactions — the same transaction(s) across all four stores, looked up by any
 * one id. MySQL is authoritative; the three ClickHouse mirrors are shown beside
 * it so a discrepancy (present here, missing there) is obvious. Which sources are
 * shown is a multi-select that persists across console views.
 */
export function Transactions({
  boot,
  tz,
  onOpenBet,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const selected = useSelectedTxSources();

  // The selectable projects — cached for the session, same as the other views.
  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const lookup = useMutation<TransactionsResult, Error, TransactionLookupInput>({
    mutationFn: (input) => fetchTransactions({ api: boot.api!, token: boot.token, input }),
  });

  const result = lookup.data;

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <FilterBar
          loading={lookup.isPending}
          onSearch={(input) => lookup.mutate(input)}
          onReset={() => lookup.reset()}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
        />
        <SourceToggle selected={selected} />
      </div>

      {lookup.isPending && <Scanning />}
      {lookup.isError && <Failure error={lookup.error} />}
      {!lookup.isPending && !lookup.isError && !result && <Idle />}

      {result && (
        <main className="rise">
          <ResolvedStrip result={result} onOpenBet={onOpenBet} />
          {TX_SOURCES.filter((s) => selected.includes(s.key)).map((s) => {
            const source = result[s.field] as unknown as SourceResult<Record<string, unknown>[]>;
            return (
              <Section key={s.key} id={`tx-${s.key}`} title={s.label} term={s.glossary}>
                <SourceBody source={source} timeKind={s.timeKind} tz={tz} />
              </Section>
            );
          })}
        </main>
      )}
    </>
  );
}

/** The persisted source multi-select. At least one stays on. */
function SourceToggle({ selected }: { selected: TxSourceKey[] }) {
  return (
    <div className="px-4 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Explain term="reconciliation" className="eyebrow shrink-0 no-underline">
          <span className="eyebrow">sources</span>
        </Explain>
        {TX_SOURCES.map((s) => {
          const on = selected.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggleTxSource(s.key)}
              className={cn(
                'inline-flex items-center gap-1.5 border px-2 py-[3px] font-condensed text-[11px] font-semibold uppercase tracking-wider transition-colors',
                on
                  ? 'border-accent/40 bg-accent/10 text-accent'
                  : 'border-hairline text-text-faint hover:text-text-dim',
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', on ? 'bg-accent' : 'bg-transparent ring-1 ring-hairline-strong')} />
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResolvedStrip({
  result,
  onOpenBet,
}: {
  result: TransactionsResult;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const r = result.resolved;

  if (!r.found) {
    return (
      <section className="flex items-center gap-2.5 border-b border-hairline bg-surface-sunken px-4 py-2.5">
        <AlertCircle size={14} className="shrink-0 text-text-faint" />
        <span className="text-[12.5px] text-text-dim">
          No transaction found in the Primary Database for that id. The three analytics copies are
          keyed off the primary record, so there is nothing to reconcile.
        </span>
      </section>
    );
  }

  const canOpen = Boolean(r.play_service_id && r.game_uuid && r.bet_id);

  return (
    <section className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-hairline bg-surface px-4 py-2.5">
      <Cell label={<Explain term="play_service_id">operator</Explain>}>
        <CopyId value={r.play_service_id} />
      </Cell>
      <Cell label={<Explain term="bet_id">bet</Explain>}>
        <CopyId value={r.bet_id} className="font-semibold" />
      </Cell>
      <Cell label={<Explain term="cycle_uniq_id">cycle</Explain>}>
        <CopyId value={r.cycle_uniq_id} truncate className="text-text-dim" />
      </Cell>
      <Cell label="matched by">
        <span className="font-condensed text-[11px] uppercase tracking-wider text-text-dim">
          {r.matchedBy.replace(/_/g, ' ')}
        </span>
      </Cell>

      {canOpen && (
        <button
          type="button"
          onClick={() =>
            onOpenBet({
              play_service_id: r.play_service_id!,
              game_uuid: r.game_uuid!,
              bet_id: r.bet_id!,
            })
          }
          className="ml-auto inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20"
        >
          <Telescope size={12} /> Inspect this bet
        </button>
      )}
    </section>
  );
}

function SourceBody({
  source,
  timeKind,
  tz,
}: {
  source: SourceResult<Record<string, unknown>[]>;
  timeKind: 'mysql' | 'clickhouse';
  tz: TimeZoneMode;
}) {
  const meta = STATUS_META[source.status];
  const rows = source.data ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-[12px]">
        <span className={meta.color}>{meta.glyph}</span>
        <span className={cn('font-condensed text-[11px] font-semibold uppercase tracking-wider', meta.color)}>
          {meta.label}
        </span>
        {source.status === 'ok' && (
          <span className="tnum font-mono text-text-faint">{rows.length} rows</span>
        )}
        {source.error && <span className="font-mono text-[11px] text-crit">{source.error}</span>}
      </div>
      {rows.length > 0 ? (
        <DataTable rows={rows} tz={tz} timeKind={timeKind} meta={null} />
      ) : (
        <p className="text-[12px] text-text-dim">
          {source.status === 'unavailable'
            ? 'Could not be reached — data may exist, we just cannot see it right now.'
            : source.status === 'skipped'
              ? 'Not queried (this source is not configured on this panel).'
              : 'No rows in this source for this transaction.'}
        </p>
      )}
    </div>
  );
}

function Cell({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="flex h-[22px] items-center">{children}</div>
    </div>
  );
}

function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        reading the transaction from every store
      </p>
    </div>
  );
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Boxes size={22} className="text-text-faint" />
      <p className="max-w-[440px] text-[13px] leading-relaxed text-text-dim">
        Pick a project, then look up a transaction by its id, tr_uniq_id, or cycle_uniq_id to see
        it as the Primary Database and each analytics copy recorded it — side by side.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  const label = status === 403 ? 'Not permitted' : 'Lookup failed';
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {label}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
